import { useStore } from "stook";
import { NoiseRecurrentValue } from "../config/constants";

/**
 * 异响信息 store
 * 位置标注：
 * - positionImg：标注点合成图上传后得到的 fileId(UUID)，提交为 reqData.noisePositionImg
 * - xPct/yPct：标注点相对图片的百分比坐标（0~1），用于返回本页时回显红点
 * 异响描述：
 * - noiseRecurrent：能否重现异响（0=否，1=是；null=未选），提交为 reqData.noiseRecurrent
 * 注：异响的问题描述文本与附件改由 useRepairIssueInfo 按子项(code=103)存储，本 store 不再持有。
 */
export interface INoiseInfo {
  positionImg: string;
  xPct: number | null;
  yPct: number | null;
  noiseRecurrent: NoiseRecurrentValue | null;
}

const INIT: INoiseInfo = {
  positionImg: "",
  xPct: null,
  yPct: null,
  noiseRecurrent: null,
};

/** 异响信息空态（初始化 / 清空提交共用），供 store 初值与提交构建复用，避免各处重写字段 */
export const INIT_NOISE_INFO: INoiseInfo = INIT;

/**
 * 组装提交用的异响字段现已在 utils/collectSubmit 中统一合并为 noiseInfo 对象。
 */
export default function useNoiseInfo() {
  const [noiseInfo, setNoiseInfo] = useStore<INoiseInfo>("NoiseInfo", INIT);

  // 设置标注点坐标（百分比）；坐标变化后原合成图失效，清空 positionImg 待重新合成
  const setNoisePosition = (xPct: number, yPct: number) =>
    setNoiseInfo((state) => {
      state.xPct = xPct;
      state.yPct = yPct;
      state.positionImg = "";
    });

  // 设置合成图上传后的 fileId
  const setNoisePositionImg = (fileId: string) =>
    setNoiseInfo((state) => {
      state.positionImg = fileId;
    });

  // 局部更新异响描述字段
  const setNoiseFields = (data: Partial<INoiseInfo>) =>
    setNoiseInfo((state) => {
      Object.assign(state, data);
    });

  // 重置（取消异响 / 跳过）
  const resetNoiseInfo = () =>
    setNoiseInfo((state) => {
      state.positionImg = "";
      state.xPct = null;
      state.yPct = null;
      state.noiseRecurrent = null;
    });

  // 编辑回显：按后端数据水合（不清空，用于按坐标重绘红点、沿用原图、回填描述表单）
  const hydrateNoiseInfo = (data: Partial<INoiseInfo>) =>
    setNoiseInfo((state) => {
      if (data.xPct != null) state.xPct = data.xPct;
      if (data.yPct != null) state.yPct = data.yPct;
      if (data.positionImg != null) state.positionImg = data.positionImg;
      if (data.noiseRecurrent != null) state.noiseRecurrent = data.noiseRecurrent;
    });

  return {
    noiseInfo,
    setNoisePosition,
    setNoisePositionImg,
    setNoiseFields,
    resetNoiseInfo,
    hydrateNoiseInfo,
  };
}
